import { z } from "zod";

const Id = z.string().min(1).max(120);
const StepId = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);
export const WorkflowCondition = z
  .object({
    path: z.string().max(180),
    operator: z.enum(["equals", "not_equals", "exists"]),
    value: z.string().max(2000).default(""),
  })
  .strict();
const common = { id: StepId, when: WorkflowCondition.optional() };
export const WorkflowStep = z.discriminatedUnion("action", [
  z
    .object({
      ...common,
      action: z.literal("run_routine"),
      routineId: Id,
      input: z.string().max(16000).default("{{ trigger }}"),
    })
    .strict(),
  z
    .object({
      ...common,
      action: z.literal("send_message"),
      text: z.string().min(1).max(20000),
      replyInThread: z.boolean().default(false),
    })
    .strict(),
  z
    .object({
      ...common,
      action: z.literal("request_approval"),
      message: z.string().min(1).max(4000),
      reviewMaterial: z.string().max(16000).default(""),
      approverUserId: Id.optional(),
      timeoutMinutes: z.number().int().min(1).max(10080).default(1440),
    })
    .strict(),
  z
    .object({ ...common, action: z.literal("delay"), seconds: z.number().int().min(1).max(604800) })
    .strict(),
]);
export const WorkflowTrigger = z.discriminatedUnion("type", [
  z.object({ type: z.literal("manual") }).strict(),
  z
    .object({
      type: z.literal("message"),
      senderUserId: Id.optional(),
      // Compatibility for old unfiltered drafts. Content filters are rejected.
      match: z.literal("any").optional(),
      text: z.literal("").optional(),
    })
    .strict(),
  z.object({ type: z.literal("reaction"), emoji: z.string().min(1).max(32) }).strict(),
  z
    .object({
      type: z.literal("schedule"),
      cron: z.string().min(1).max(100),
      timezone: z.string().min(1).max(100).default("UTC"),
    })
    .strict(),
  z.object({ type: z.literal("webhook") }).strict(),
]);
export const WorkflowDefinition = z
  .object({
    version: z.literal(1),
    name: z.string().trim().min(1).max(100),
    description: z.string().max(1000).default(""),
    channelId: Id.optional(),
    trigger: WorkflowTrigger,
    steps: z.array(WorkflowStep).min(1).max(30),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (
      (["message", "reaction"].includes(d.trigger.type) ||
        d.steps.some((s) => s.action === "send_message")) &&
      !d.channelId
    )
      ctx.addIssue({ code: "custom", message: "Choose a channel for this workflow." });
    const ids = new Set<string>();
    for (const step of d.steps) {
      if (ids.has(step.id)) ctx.addIssue({ code: "custom", message: "Step IDs must be unique." });
      const validPath = (path: string) => {
        const parts = path.split(".");
        if (parts.some((p) => ["__proto__", "prototype", "constructor"].includes(p))) return false;
        return (
          parts[0] === "trigger" ||
          (parts[0] === "steps" && !!parts[1] && ids.has(parts[1]) && parts[2] === "output") ||
          workflowConditionOptions(d.steps.slice(0, d.steps.indexOf(step))).some(
            (o) => o.path === path,
          )
        );
      };
      if (
        step.when &&
        !workflowConditionOptions(d.steps.slice(0, d.steps.indexOf(step))).some(
          (o) => o.path === step.when!.path,
        )
      )
        ctx.addIssue({
          code: "custom",
          message: `${step.id}: conditions can test declared facts only. Message text, webhook bodies and routine prose cannot be conditions.`,
        });
      for (const value of Object.values(step))
        if (typeof value === "string") {
          for (const match of value.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g))
            if (!validPath(match[1]!.trim()))
              ctx.addIssue({
                code: "custom",
                message: `${step.id}: invalid template reference ${match[1]}.`,
              });
          if (value.replace(/\{\{[^{}]+\}\}/g, "").includes("{{"))
            ctx.addIssue({ code: "custom", message: `${step.id}: incomplete template.` });
        }
      ids.add(step.id);
    }
  });
export type WorkflowDefinitionValue = z.infer<typeof WorkflowDefinition>;
export type WorkflowStepValue = z.infer<typeof WorkflowStep>;
export const WORKFLOW_TRIGGER_FACTS = [
  "type",
  "userId",
  "channelId",
  "messageId",
  "emoji",
  "scheduledAt",
  "at",
] as const;
export function workflowConditionOptions(earlier: readonly WorkflowStepValue[]) {
  const labels: Record<string, string> = {
    type: "Trigger type",
    userId: "Sender ID",
    channelId: "Channel ID",
    messageId: "Message ID",
    emoji: "Reaction",
    scheduledAt: "Scheduled time",
    at: "Event time",
  };
  return [
    ...WORKFLOW_TRIGGER_FACTS.map((key) => ({ path: `trigger.${key}`, label: labels[key]! })),
    ...earlier.flatMap((step) => [
      { path: `steps.${step.id}.status`, label: `${step.id} · Step status` },
      ...(step.action === "run_routine"
        ? [{ path: `steps.${step.id}.runId`, label: `${step.id} · Bot run ID` }]
        : []),
      ...(step.action === "request_approval"
        ? [{ path: `steps.${step.id}.approved`, label: `${step.id} · Approved` }]
        : []),
      ...(step.action === "delay"
        ? [{ path: `steps.${step.id}.elapsed`, label: `${step.id} · Delay completed` }]
        : []),
      ...(step.action === "send_message"
        ? [{ path: `steps.${step.id}.messageId`, label: `${step.id} · Posted message ID` }]
        : []),
    ]),
  ];
}
export const WorkflowTarget = z.object({ id: Id }).strict();
export const WorkflowSave = z
  .object({
    id: Id.optional(),
    revision: z.number().int().positive().optional(),
    definition: WorkflowDefinition,
  })
  .strict();
export const WorkflowView = z.object({
  id: Id,
  revision: z.number(),
  enabled: z.boolean(),
  definition: WorkflowDefinition,
  webhookConfigured: z.boolean(),
  nextRunAt: z.string().nullable(),
  updatedAt: z.string(),
  blockedReason: z.string().nullable(),
  source: z.string().nullable(),
});
export const WorkflowToggle = WorkflowTarget.extend({
  revision: z.number().int().positive(),
  enabled: z.boolean(),
}).strict();
export const WorkflowRunNow = WorkflowTarget.extend({
  nonce: z.string().uuid(),
  data: z.record(z.string(), z.unknown()).default({}),
}).strict();
export const WorkflowRunTarget = z.object({ runId: Id }).strict();
const Hash = z.string().regex(/^[0-9a-f]{64}$/);
export const WorkflowDecision = z
  .object({
    approvalId: Id,
    reviewHash: Hash,
    approve: z.boolean(),
  })
  .strict();
export const WorkflowReviewPacket = z
  .object({
    protocol: z.literal("wurkees.workflow-review/v1"),
    approvalId: Id,
    spaceId: Id,
    workflowId: Id,
    runId: Id,
    stepId: StepId,
    revision: z.number().int().positive(),
    ownerUserId: Id,
    approverUserId: Id,
    ownerMembershipId: Id,
    approverMembershipId: Id,
    definitionHash: Hash,
    title: z.string().max(100),
    ownerName: z.string().max(200),
    approverName: z.string().max(200),
    message: z.string().max(4000),
    material: z.string().max(16000),
    continuation: z
      .array(
        z
          .object({
            id: StepId,
            action: z.enum(["run_routine", "send_message", "request_approval", "delay"]),
          })
          .strict(),
      )
      .max(30),
    createdAt: z.string(),
    expiresAt: z.string(),
  })
  .strict();
export const WorkflowApprovalSummary = z.object({
  id: Id.nullable(),
  outcome: z.enum(["pending", "approved", "declined", "expired", "cancelled", "invalidated"]),
  approverUserId: Id,
  approverName: z.string(),
  reviewHash: Hash.nullable(),
  decidedByUserId: Id.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  canDecide: z.boolean(),
  legacy: z.boolean(),
  reason: z.string().nullable(),
  review: WorkflowReviewPacket.nullable(),
});
export const WorkflowApprovalCard = WorkflowApprovalSummary.extend({
  id: Id,
  reviewHash: Hash,
  review: WorkflowReviewPacket,
  outcome: z.literal("pending"),
});
export type WorkflowReviewPacketValue = z.infer<typeof WorkflowReviewPacket>;
export type WorkflowApprovalSummaryValue = z.infer<typeof WorkflowApprovalSummary>;
export function canonicalWorkflowReview(packet: WorkflowReviewPacketValue): string {
  return JSON.stringify(packet, (_key, value: unknown) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : value,
  );
}
export const WorkflowRunView = z.object({
  id: Id,
  workflowId: Id,
  name: z.string(),
  revision: z.number(),
  status: z.string(),
  trigger: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  wakeAt: z.string().nullable(),
  steps: z.array(
    z.object({
      id: StepId,
      action: z.string(),
      status: z.string(),
      output: z.string().nullable(),
      error: z.string().nullable(),
      botRunId: z.string().nullable(),
      botId: z.string().nullable(),
      threadId: z.string().nullable(),
      startedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
      approval: WorkflowApprovalSummary.nullable(),
    }),
  ),
});
export const WorkflowReaction = z
  .object({
    channelId: Id,
    messageId: Id,
    emoji: z.enum(["👍", "👎", "❤️", "😂", "🎉", "😮", "✅", "👀", "🚀"]),
    add: z.boolean(),
  })
  .strict();
export const WorkflowReactions = z.object({
  messageId: Id,
  reactions: z.array(z.object({ emoji: z.string(), count: z.number(), own: z.boolean() })),
});
